import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  Link,
  Breadcrumbs,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';

/**
 * GDPR Readiness Statement page component
 * 
 * Contains the GDPR compliance framework and readiness statement
 * for the MyEventShop platform
 */
const GDPRReadiness: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };

  // Table of contents items
  const tableOfContents = [
    { title: '1. Data Inventory, Mapping, and Records of Processing Activities (RoPA)', id: 'ropa' },
    { title: '2. Privacy by Design and Default (Article 25)', id: 'privacy-design' },
    { title: '3. Data Protection Impact Assessments (DPIAs) and Risk Management', id: 'dpias' },
    { title: '4. Data Subject Rights Management (Articles 12–23)', id: 'data-rights' },
    { title: '5. International Data Transfers and Standard Contractual Clauses (SCCs)', id: 'transfers' },
    { title: '6. Data Retention and Secure Deletion', id: 'retention' },
    { title: '7. Technical and Organisational measures', id: 'technical' },
    { title: '8. Third-Party Vendor Management', id: 'vendors' },
    { title: '9. Ongoing Monitoring, Audits, and Continuous Improvement', id: 'monitoring' },
    { title: '10. Contact Information', id: 'contact' }
  ];

  return (
    <Box>
      <AppMenu 
        onLoginClick={() => setIsLoginOpen(true)}
        onSignupClick={() => setIsSignupOpen(true)} 
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mt: 3, mb: 2 }}
        >
          <Link 
            underline="hover" 
            color="inherit" 
            onClick={() => navigate('/')}
            sx={{ cursor: 'pointer' }}
          >
            Home
          </Link>
          <Typography color="text.primary">GDPR Readiness Statement</Typography>
        </Breadcrumbs>

        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            color="primary" 
            fontWeight="bold"
            gutterBottom
          >
            GDPR Readiness Statement
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px' }}>
            MyEventShop is committed to full compliance with the General Data Protection Regulation (GDPR) and has established a robust data protection and privacy program to ensure the lawful, secure, and transparent handling of personal data. Our approach is grounded in continuous improvement, privacy engineering, and adherence to global security standards.
          </Typography>
        </Box>

        {/* Main Content Container */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 8 }}>
          {/* Table of Contents */}
          <Box 
            component={Paper} 
            elevation={2}
            sx={{
              p: 3, 
              borderRadius: 2, 
              width: { xs: '100%', md: '300px' },
              flexShrink: 0,
              position: { xs: 'static', md: 'sticky' },
              top: '20px',
              alignSelf: 'flex-start',
              maxHeight: { xs: 'auto', md: 'calc(100vh - 40px)' },
              overflowY: { xs: 'visible', md: 'auto' }
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2}>Table of Contents</Typography>
            <List sx={{ p: 0 }}>
              {tableOfContents.map((item) => (
                <ListItem 
                  key={item.id} 
                  disablePadding 
                  sx={{ mb: 1 }}
                >
                  <ListItemText 
                    primary={
                      <Link 
                        href={`#${item.id}`} 
                        underline="hover" 
                        color="text.primary"
                        sx={{ 
                          display: 'block',
                          fontSize: '0.9rem',
                          lineHeight: 1.4,
                        }}
                      >
                        {item.title}
                      </Link>
                    } 
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* GDPR Content */}
          <Box 
            component={Paper}
            elevation={2}
            sx={{ 
              p: { xs: 3, md: 4 }, 
              borderRadius: 2,
              flex: '1 1 auto',
            }}
          >
            <Typography variant="h5" fontWeight="bold" gutterBottom mb={3}>
              Key Components of Our GDPR Compliance Framework
            </Typography>

            <Box id="ropa" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                1. Data Inventory, Mapping, and Records of Processing Activities (RoPA)
              </Typography>
              <Typography variant="body1" paragraph>
                We maintain a comprehensive Record of Processing Activities (RoPA) as mandated by GDPR. This record is continuously updated to reflect data flows, processing purposes, and retention periods for all personal data across our organization.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="privacy-design" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                2. Privacy by Design and Default (Article 25)
              </Typography>
              <Typography variant="body1" paragraph>
                Privacy and data protection are embedded into the design of our systems and processes. By default, we minimize the collection and storage of personal data and ensure all data processing is purpose specific. This includes employing techniques such as pseudonymization, organisational controls, data masking, and encryption.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="dpias" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                3. Data Protection Impact Assessments (DPIAs) and Risk Management
              </Typography>
              <Typography variant="body1" paragraph>
                For high-risk processing activities, we conduct Data Protection Impact Assessments (DPIAs) in line with Article 35 to identify risks and implement appropriate mitigation strategies. This risk-based approach ensures compliance while safeguarding individual rights.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="data-rights" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                4. Data Subject Rights Management (Articles 12–23)
              </Typography>
              <Typography variant="body1" paragraph>
                Our Data Subject Access Request (DSAR) management system ensures timely responses to data subject requests. This includes rights such as:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Access:</strong> Providing copies of personal data upon request</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Rectification and Erasure:</strong> Correcting inaccurate data and erasing data when no longer needed</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Data Portability:</strong> Delivering personal data in a structured, machine-readable format</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Objection to Processing and Automated Decision-Making:</strong> Ensuring opt-out options for automated processing and profiling</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="transfers" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                5. International Data Transfers and Standard Contractual Clauses (SCCs)
              </Typography>
              <Typography variant="body1" paragraph>
                Where personal data is transferred outside the European Economic Area (EEA), we adhere to Chapter V of the GDPR. We utilize Standard Contractual Clauses (SCCs), ensuring adequate safeguards for cross-border data flows. Additionally, Transfer Impact Assessments (TIAs) are performed to evaluate risks in third countries.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="retention" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                6. Data Retention and Secure Deletion
              </Typography>
              <Typography variant="body1" paragraph>
                Our data retention policies are based on the principles of data minimization and storage limitation (Article 5(1)(e)). We enforce automated data deletion schedules to ensure personal data is deleted once it is no longer required for its original purpose, in accordance with legal and regulatory requirements. Secure deletion processes are applied to both physical and digital records.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="technical" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                7. Technical and Organisational measures
              </Typography>
              <Typography variant="body1" paragraph>
                We have implemented a range of technical and organizational measures (TOMs) in compliance with Article 32 to protect personal data. These include:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Encryption (AES-256 and TLS 1.3) for data at rest and in transit</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Multi-factor authentication (MFA) and role-based access control (RBAC)</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Network segmentation and threat detection systems</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Security Incident and Event Management (SIEM) for continuous monitoring and response</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="vendors" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                8. Third-Party Vendor Management
              </Typography>
              <Typography variant="body1" paragraph>
                All third-party processors undergo strict vetting and due diligence to ensure compliance with GDPR. We have implemented Data Processing Agreements (DPAs) and continuously monitor vendor compliance through regular audits and assessments.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="monitoring" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                9. Ongoing Monitoring, Audits, and Continuous Improvement
              </Typography>
              <Typography variant="body1" paragraph>
                Compliance is an ongoing process. We regularly audit our GDPR framework to identify areas for improvement. Our internal compliance team persistently works to ensure adherence to evolving regulatory requirements. Any gaps are addressed through a structured corrective action process.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="contact" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                10. Contact Information
              </Typography>
              <Typography variant="body1" paragraph>
                For more information on our GDPR compliance program, international data transfer mechanisms, or to exercise your data protection rights, please contact <Link href="mailto:admin@myeventshop.com" color="primary">admin@myeventshop.com</Link>.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
      
      {/* Authentication Modals */}
      <LoginModal 
        open={isLoginOpen} 
        onClose={() => setIsLoginOpen(false)} 
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal 
        open={isSignupOpen} 
        onClose={() => setIsSignupOpen(false)} 
        onSwitchToLogin={handleSwitchToLogin}
      />
    </Box>
  );
};

export default GDPRReadiness;

